import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useQueryClient } from '@tanstack/react-query';
import { IAdminTask, IAdminTaskAssignee, TASKS_QUERY_KEY, TaskType, createTask } from '../../api/admin';
import { apiErrorMessage } from '../../utils/api';
import TaskAssigneeLabel from './TaskAssigneeLabel';
import { DEFAULT_TASK_TYPE, TASK_TYPE_OPTIONS } from './taskOptions';

interface Props {
	/** Who can be picked as the assignee: admins holding the tasks permission. */
	assignees: IAdminTaskAssignee[];
	/** Pre-selected assignee, normally the signed-in admin. */
	defaultAssigneeId: number | null;
	onAdded?: (task: IAdminTask) => void;
}

/**
 * One line to add a task fast: title, type, assignee, Enter. The task starts open and
 * untagged; change either later in the grid or the full form. Type and assignee stick
 * between adds so a batch for one person is just typing.
 */
export default function TaskQuickAdd({ assignees, defaultAssigneeId, onAdded }: Props) {
	const queryClient = useQueryClient();
	const inputRef = useRef<HTMLInputElement>(null);
	const [title, setTitle] = useState('');
	const [type, setType] = useState<TaskType>(DEFAULT_TASK_TYPE);
	const [assigneeId, setAssigneeId] = useState<number | ''>(defaultAssigneeId ?? '');
	const [assigneeTouched, setAssigneeTouched] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// The signed-in admin's id arrives with the users query; adopt it until someone else is picked.
	useEffect(() => {
		if (!assigneeTouched) setAssigneeId(defaultAssigneeId ?? '');
	}, [defaultAssigneeId, assigneeTouched]);

	async function submit() {
		const trimmed = title.trim();
		if (!trimmed || submitting) return;
		setSubmitting(true);
		setError(null);
		try {
			const task = await createTask({
				title: trimmed,
				type,
				assignee_user_id: assigneeId === '' ? null : assigneeId,
			});
			await queryClient.invalidateQueries(TASKS_QUERY_KEY);
			setTitle('');
			onAdded?.(task);
		} catch (e: any) {
			setError(apiErrorMessage(e, 'Could not add the task. Please try again.'));
		} finally {
			setSubmitting(false);
			inputRef.current?.focus();
		}
	}

	return (
		<Box sx={{ mb: 2 }}>
			<Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
				<TextField
					inputRef={inputRef}
					placeholder="Add a task and press Enter"
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
							submit();
						} else if (e.key === 'Escape') {
							setTitle('');
						}
					}}
					disabled={submitting}
					inputProps={{ 'aria-label': 'New task title', maxLength: 200 }}
					sx={{ flex: 1, minWidth: 260 }}
				/>
				<TextField
					select
					label="Type"
					value={type}
					onChange={(e) => setType(e.target.value as TaskType)}
					sx={{ width: 170 }}
				>
					{TASK_TYPE_OPTIONS.map((o) => (
						<MenuItem key={o.value} value={o.value}>
							{o.label}
						</MenuItem>
					))}
				</TextField>
				<TextField
					select
					label="Assignee"
					value={assigneeId}
					onChange={(e) => {
						setAssigneeTouched(true);
						setAssigneeId(e.target.value === '' ? '' : Number(e.target.value));
					}}
					sx={{ width: 190 }}
				>
					<MenuItem value="">Unassigned</MenuItem>
					{assignees.map((u) => (
						<MenuItem key={u.id} value={u.id}>
							<TaskAssigneeLabel user={u} assignees={assignees} />
						</MenuItem>
					))}
				</TextField>
				<Button
					variant="contained"
					size="large"
					onClick={submit}
					disabled={!title.trim() || submitting}
					startIcon={submitting ? <CircularProgress size={16} /> : undefined}
				>
					Add
				</Button>
			</Box>
			{error && (
				<Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			)}
		</Box>
	);
}
